import { useState, useCallback } from "react";
import { useGraphQLFetch } from "./use-graphql-fetch";
import { StockItem } from "@/types/stock-item";
import { toast } from "sonner";

// Pagination info from GraphQL connection
export interface PageInfo {
  hasNextPage: boolean;
  hasPreviousPage: boolean;
  startCursor: string | null;
  endCursor: string | null;
}

// Sort direction type (matching backend OrderType enum)
export type SortDirection = "Ascending" | "Descending";

// Sort field enum values (matching backend StockItemSort enum)
export type StockItemSortField =
  | "StockCode"
  | "Description"
  | "Category"
  | "PhysicalQuantity"
  | "UnitPrice"
  | "TotalValue"
  | "LastMovementDate";

// Sort input for stock items - uses field/order structure expected by backend
export interface StockItemSortInput {
  field: StockItemSortField;
  order: SortDirection;
}

// Filter input for stock items
export interface StockItemFilterInput {
  stockCode?: { contains?: string; eq?: string };
  description?: { contains?: string };
  category?: { contains?: string; eq?: string };
  barcode?: { contains?: string; eq?: string };
  inactive?: { eq?: boolean };
}

// GraphQL response types
interface StockItemNode {
  stockItemID: number;
  stockCode: string;
  description: string;
  category: string | null;
  location: string | null;
  warehouseCode: string | null;
  locationCode: string | null;
  barcode: string | null;
  imageURL: string | null;
  physicalQuantity: number;
  allocatedQuantity: number;
  freeQuantity: number;
  quantityDueIn: number;
  minQuantity: number;
  maxQuantity: number;
  reorderPoint: number;
  reorderQuantity: number;
  unitPrice: number;
  lastCostPrice: number;
  averageCost: number;
  totalValue: number;
  grossMarginPercent: number;
  totalCostOfSales: number;
  totalValueOfSales: number;
  weight: number | null;
  weightUnit: string | null;
  binNumber: string | null;
  unitOfMeasure: string;
  packSize: number | null;
  cartonQuantity: number | null;
  weightPerUnit: number | null;
  weightPerUnitUnit: string | null;
  dimensionLength: number | null;
  dimensionWidth: number | null;
  dimensionHeight: number | null;
  dimensionUnit: string | null;
  cubicVolume: number | null;
  taxRateId: string | null;
  taxRate: number | null;
  taxRateName: string | null;
  purchaseAnalysisCode: string | null;
  salesAnalysisCode: string | null;
  movementInAnalysis: string | null;
  movementOutAnalysis: string | null;
  aBCClassification: string | null;
  stockTurnRate: number | null;
  supplier: string | null;
  supplierContact: string | null;
  leadTimeDays: number | null;
  lastPurchaseDate: string | null;
  lastPurchaseQuantity: number | null;
  lastPurchasePrice: number | null;
  lastSaleDate: string | null;
  lastSaleQuantity: number | null;
  lastSalePrice: number | null;
  onTimeDeliveryRate: number | null;
  averageLeadTime: number | null;
  totalOrdersYTD: number | null;
  inactive: boolean;
  status: string;
  lastMovementDate: string | null;
  createdAt: string;
  modifiedAt: string;
  customFields: Array<{ fieldName: string; fieldValue: string }> | null;
}

// GraphQL connection types for stock items
interface StockItemConnection {
  items: StockItemNode[];
  pageInfo: PageInfo;
  totalCount?: number;
}

interface GraphQLResponse {
  data?: {
    stockItems?: {
      stockItemList?: StockItemConnection;
    };
  };
  errors?: Array<{ message: string }>;
}

// Map backend response to frontend StockItem type
const mapBackendToFrontend = (node: StockItemNode): StockItem => {
  return {
    stockItemID: node.stockItemID,
    code: node.stockCode,
    description: node.description,
    category: node.category || "",
    warehouseCode: node.warehouseCode || "",
    locationCode: node.locationCode || "",
    physicalQuantity: node.physicalQuantity,
    unitPrice: node.unitPrice,
    value: node.totalValue,
    inactive: node.inactive,
    lastMovementDate: node.lastMovementDate ? new Date(node.lastMovementDate) : new Date(),
    barcode: node.barcode || "",
    image: node.imageURL || undefined,
    weight: node.weight || undefined,
    weightUnit: (node.weightUnit as "kg" | "g" | "lbs") || undefined,
    binNumber: node.binNumber || undefined,
    allocatedQuantity: node.allocatedQuantity,
    quantityDueIn: node.quantityDueIn,
    minQuantity: node.minQuantity,
    maxQuantity: node.maxQuantity,
    reorderPoint: node.reorderPoint,
    reorderQuantity: node.reorderQuantity,
    lastCostPrice: node.lastCostPrice,
    averageCost: node.averageCost,
    totalCostOfSales: node.totalCostOfSales,
    totalValueOfSales: node.totalValueOfSales,
    purchaseAnalysisCode: node.purchaseAnalysisCode || undefined,
    salesAnalysisCode: node.salesAnalysisCode || undefined,
    movementInAnalysis: node.movementInAnalysis || undefined,
    movementOutAnalysis: node.movementOutAnalysis || undefined,
    abcClassification: (node.aBCClassification as "A" | "B" | "C") || "C",
    stockTurnRate: node.stockTurnRate || 0,
    supplier: node.supplier || undefined,
    leadTimeDays: node.leadTimeDays || undefined,
    lastPurchaseDate: node.lastPurchaseDate ? new Date(node.lastPurchaseDate) : null,
    lastSaleDate: node.lastSaleDate ? new Date(node.lastSaleDate) : null,
    supplierContact: node.supplierContact || undefined,
    lastPurchaseQuantity: node.lastPurchaseQuantity || undefined,
    lastPurchasePrice: node.lastPurchasePrice || undefined,
    lastSaleQuantity: node.lastSaleQuantity || undefined,
    lastSalePrice: node.lastSalePrice || undefined,
    onTimeDeliveryRate: node.onTimeDeliveryRate || undefined,
    averageLeadTime: node.averageLeadTime || undefined,
    totalOrdersYTD: node.totalOrdersYTD || undefined,
    unitOfMeasure: (node.unitOfMeasure as StockItem["unitOfMeasure"]) || "Each",
    packSize: node.packSize || undefined,
    cartonQuantity: node.cartonQuantity || undefined,
    weightPerUnit: node.weightPerUnit || undefined,
    weightPerUnitUnit: (node.weightPerUnitUnit as "kg" | "g" | "lbs") || "kg",
    dimensionLength: node.dimensionLength || undefined,
    dimensionWidth: node.dimensionWidth || undefined,
    dimensionHeight: node.dimensionHeight || undefined,
    dimensionUnit: (node.dimensionUnit as "cm" | "m" | "in" | "ft") || "cm",
    cubicVolume: node.cubicVolume || undefined,
    taxRateId: node.taxRateId || undefined,
    taxRate: node.taxRate || undefined,
    taxRateName: node.taxRateName || undefined,
    // Convert list of {fieldName, fieldValue} objects to Record<string, string>
    customFields:
      node.customFields?.reduce((acc, cf) => {
        acc[cf.fieldName] = cf.fieldValue;
        return acc;
      }, {} as Record<string, string>) || undefined,
  };
};

// GraphQL query for stock items with filters, sorting, and pagination
const STOCK_ITEMS_QUERY = `
  query GetStockItems(
    $first: Int, 
    $after: String, 
    $filter: StockItemFilterInput,
    $sort: [StockItemSortSortByInput!]
  ) {
    stockItems {
      stockItemList(
        first: $first, 
        after: $after, 
        filter: $filter,
        sort: $sort
      ) {
        items {
          stockItemID
          stockCode
          description
          category
          location
          warehouseCode
          locationCode
          barcode
          imageURL
          physicalQuantity
          allocatedQuantity
          freeQuantity
          quantityDueIn
          minQuantity
          maxQuantity
          reorderPoint
          reorderQuantity
          unitPrice
          lastCostPrice
          averageCost
          totalValue
          grossMarginPercent
          totalCostOfSales
          totalValueOfSales
          weight
          weightUnit
          binNumber
          unitOfMeasure
          packSize
          cartonQuantity
          weightPerUnit
          weightPerUnitUnit
          dimensionLength
          dimensionWidth
          dimensionHeight
          dimensionUnit
          cubicVolume
          taxRateId
          taxRate
          taxRateName
          purchaseAnalysisCode
          salesAnalysisCode
          movementInAnalysis
          movementOutAnalysis
          aBCClassification
          stockTurnRate
          supplier
          supplierContact
          leadTimeDays
          lastPurchaseDate
          lastPurchaseQuantity
          lastPurchasePrice
          lastSaleDate
          lastSaleQuantity
          lastSalePrice
          onTimeDeliveryRate
          averageLeadTime
          totalOrdersYTD
          inactive
          status
          lastMovementDate
          createdAt
          modifiedAt
          customFields {
            fieldName
            fieldValue
          }
        }
        pageInfo {
          hasNextPage
          hasPreviousPage
          startCursor
          endCursor
        }
      }
    }
  }
`;

// GraphQL query to fetch a single stock item by stock code
const STOCK_ITEM_BY_CODE_QUERY = `
  query GetStockItemByCode($stockCode: String!) {
    stockItems {
      stockItemList(filter: { stockCode: { eq: $stockCode } }, first: 1) {
        items {
          stockItemID
          stockCode
          description
          category
          location
          warehouseCode
          locationCode
          barcode
          imageURL
          physicalQuantity
          allocatedQuantity
          freeQuantity
          quantityDueIn
          minQuantity
          maxQuantity
          reorderPoint
          reorderQuantity
          unitPrice
          lastCostPrice
          averageCost
          totalValue
          grossMarginPercent
          totalCostOfSales
          totalValueOfSales
          weight
          weightUnit
          binNumber
          unitOfMeasure
          packSize
          cartonQuantity
          weightPerUnit
          weightPerUnitUnit
          dimensionLength
          dimensionWidth
          dimensionHeight
          dimensionUnit
          cubicVolume
          taxRateId
          taxRate
          taxRateName
          purchaseAnalysisCode
          salesAnalysisCode
          movementInAnalysis
          movementOutAnalysis
          aBCClassification
          stockTurnRate
          supplier
          supplierContact
          leadTimeDays
          lastPurchaseDate
          lastPurchaseQuantity
          lastPurchasePrice
          lastSaleDate
          lastSaleQuantity
          lastSalePrice
          onTimeDeliveryRate
          averageLeadTime
          totalOrdersYTD
          inactive
          status
          lastMovementDate
          createdAt
          modifiedAt
          customFields {
            fieldName
            fieldValue
          }
        }
      }
    }
  }
`;

// GraphQL query to fetch stock item settings by stock code
const STOCK_ITEM_SETTINGS_BY_CODE_QUERY = `
  query GetStockItemSettingsByCode($stockCode: String!) {
    stockItemSettings {
      stockItemSettingsByCode(stockCode: $stockCode) {
        stockItemSettingID
        stockItemID
        stockCode
        isSellable
        isPurchasable
        isAssembly
        isComponent
        allowDisassembly
        trackingType
        autoGenerateSerial
        serialPrefix
        serialNextNumber
        requireExpiryDate
        requireManufacturingDate
        shelfLifeDays
        expiryWarningDays
        allowNegativeStock
        minimumOrderQuantity
        maximumOrderQuantity
        orderMultiple
        requireQualityCheck
        quarantineDays
        allowPriceOverride
        minimumMarginPercent
        maximumDiscountPercent
      }
    }
  }
`;

// Response type for settings query
interface StockItemSettingsQueryResponse {
  stockItemSettings?: {
    stockItemSettingsByCode?: {
      stockItemSettingID: number;
      stockItemID: number;
      stockCode: string;
      isSellable: boolean;
      isPurchasable: boolean;
      isAssembly: boolean;
      isComponent: boolean;
      allowDisassembly: boolean;
      trackingType: string;
      autoGenerateSerial: boolean;
      serialPrefix?: string;
      serialNextNumber?: number;
      requireExpiryDate: boolean;
      requireManufacturingDate: boolean;
      shelfLifeDays?: number;
      expiryWarningDays?: number;
      allowNegativeStock: boolean;
      minimumOrderQuantity?: number;
      maximumOrderQuantity?: number;
      orderMultiple?: number;
      requireQualityCheck: boolean;
      quarantineDays?: number;
      allowPriceOverride: boolean;
      minimumMarginPercent?: number;
      maximumDiscountPercent?: number;
    };
  };
}

// GraphQL mutation to update a stock item
const UPDATE_STOCK_ITEM_MUTATION = `
  mutation UpdateStockItem($stockItem: StockItemInput!) {
    stockItems {
      stockItemsRecords {
        update(stockItem: $stockItem) {
          __typename
          ... on IntEntityId {
            id
          }
          ... on FailedValidation {
            errors {
              message
            }
          }
        }
      }
    }
  }
`;

// GraphQL mutation to create a stock item
const CREATE_STOCK_ITEM_MUTATION = `
  mutation CreateStockItem($stockItem: StockItemInput!) {
    stockItems {
      stockItemsRecords {
        create(stockItem: $stockItem) {
          __typename
          ... on IntEntityId {
            id
          }
          ... on FailedValidation {
            errors {
              message
            }
          }
        }
      }
    }
  }
`;

// GraphQL mutation to delete stock items
const DELETE_STOCK_ITEMS_MUTATION = `
  mutation DeleteStockItems($ids: [Int!]) {
    stockItems {
      stockItemsRecords {
        delete(ids: $ids) {
          __typename
          ... on Succeeded {
            succeeded
          }
        }
      }
    }
  }
`;

// GraphQL mutation to update stock item settings
const UPDATE_STOCK_ITEM_SETTINGS_MUTATION = `
  mutation UpdateStockItemSettings($settings: StockItemSettingsInput!) {
    stockItemSettings {
      stockItemSettingsRecords {
        update(settings: $settings) {
          __typename
          ... on IntEntityId {
            id
          }
        }
      }
    }
  }
`;

// Input type for stock item settings
interface StockItemSettingsInput {
  stockItemSettingID: number;
  stockItemID: number;
  stockCode: string;
  isSellable: boolean;
  isPurchasable: boolean;
  isAssembly: boolean;
  isComponent: boolean;
  allowDisassembly: boolean;
  trackingType: string;
  autoGenerateSerial: boolean;
  serialPrefix?: string | null;
  serialNextNumber?: number | null;
  requireExpiryDate: boolean;
  requireManufacturingDate: boolean;
  shelfLifeDays?: number | null;
  expiryWarningDays?: number | null;
  allowNegativeStock: boolean;
  minimumOrderQuantity?: number | null;
  maximumOrderQuantity?: number | null;
  orderMultiple?: number | null;
  requireQualityCheck: boolean;
  quarantineDays?: number | null;
  allowPriceOverride: boolean;
  minimumMarginPercent?: number | null;
  maximumDiscountPercent?: number | null;
}

// Response type for settings update mutation
interface UpdateSettingsMutationResponse {
  data?: {
    stockItemSettings?: {
      stockItemSettingsRecords?: {
        update?: {
          __typename?: string;
          id?: number;
        };
      };
    };
  };
  errors?: Array<{ message: string }>;
}

// Input type for updating a stock item (matches backend StockItemInput)
interface StockItemUpdateInput {
  stockItemID: number;
  stockCode: string;
  description: string;
  category?: string | null;
  location?: string | null;
  warehouseCode?: string | null;
  locationCode?: string | null;
  barcode?: string | null;
  imageURL?: string | null;
  physicalQuantity: number;
  allocatedQuantity: number;
  quantityDueIn: number;
  minQuantity: number;
  maxQuantity: number;
  reorderPoint: number;
  reorderQuantity: number;
  unitPrice: number;
  // Cost prices - required by backend, typically derived from stock movements
  lastCostPrice: number;
  averageCost: number;
  totalValue: number;
  weight?: number | null;
  weightUnit?: string | null;
  binNumber?: string | null;
  // Unit of Measure and Packaging
  unitOfMeasure: string;
  packSize?: number | null;
  cartonQuantity?: number | null;
  weightPerUnit?: number | null;
  weightPerUnitUnit?: string | null;
  dimensionLength?: number | null;
  dimensionWidth?: number | null;
  dimensionHeight?: number | null;
  dimensionUnit?: string | null;
  cubicVolume?: number | null;
  purchaseAnalysisCode?: string | null;
  salesAnalysisCode?: string | null;
  movementInAnalysis?: string | null;
  movementOutAnalysis?: string | null;
  aBCClassification?: string | null;
  supplier?: string | null;
  supplierContact?: string | null;
  leadTimeDays?: number | null;
  inactive: boolean;
}

// Input type for creating a stock item (no stockItemID required)
interface StockItemCreateInput {
  stockCode: string;
  description: string;
  category?: string | null;
  location?: string | null;
  warehouseCode?: string | null;
  locationCode?: string | null;
  barcode?: string | null;
  imageURL?: string | null;
  physicalQuantity: number;
  allocatedQuantity: number;
  quantityDueIn: number;
  minQuantity: number;
  maxQuantity: number;
  reorderPoint: number;
  reorderQuantity: number;
  unitPrice: number;
  // Cost prices - can be set during duplication to copy from original item
  lastCostPrice?: number;
  averageCost?: number;
  totalValue?: number;
  weight?: number | null;
  weightUnit?: string | null;
  binNumber?: string | null;
  // Unit of Measure and Packaging
  unitOfMeasure: string;
  packSize?: number | null;
  cartonQuantity?: number | null;
  weightPerUnit?: number | null;
  weightPerUnitUnit?: string | null;
  dimensionLength?: number | null;
  dimensionWidth?: number | null;
  dimensionHeight?: number | null;
  dimensionUnit?: string | null;
  cubicVolume?: number | null;
  purchaseAnalysisCode?: string | null;
  salesAnalysisCode?: string | null;
  movementInAnalysis?: string | null;
  movementOutAnalysis?: string | null;
  aBCClassification?: string | null;
  supplier?: string | null;
  supplierContact?: string | null;
  leadTimeDays?: number | null;
  inactive: boolean;
}

// Response type for update mutation
interface UpdateMutationResponse {
  data?: {
    stockItems?: {
      stockItemsRecords?: {
        update?: {
          __typename?: string;
          id?: number;
        };
      };
    };
  };
  errors?: Array<{ message: string }>;
}

// Response type for create mutation
interface CreateMutationResponse {
  data?: {
    stockItems?: {
      stockItemsRecords?: {
        create?: {
          __typename?: string;
          id?: number;
        };
      };
    };
  };
  errors?: Array<{ message: string }>;
}

// Response type for delete mutation
interface DeleteMutationResponse {
  data?: {
    stockItems?: {
      stockItemsRecords?: {
        delete?: {
          __typename?: string;
          succeeded?: boolean;
        };
      };
    };
  };
  errors?: Array<{ message: string }>;
}

// Map frontend StockItem to backend input format for update
const mapFrontendToBackendInput = (item: StockItem): StockItemUpdateInput => {
  return {
    stockItemID: item.stockItemID || 0,
    stockCode: item.code,
    description: item.description,
    category: item.category || null,
    location: item.locationCode || null,
    warehouseCode: item.warehouseCode || null,
    locationCode: item.locationCode || null,
    barcode: item.barcode || null,
    imageURL: item.image || null,
    physicalQuantity: item.physicalQuantity,
    allocatedQuantity: item.allocatedQuantity,
    quantityDueIn: item.quantityDueIn,
    minQuantity: item.minQuantity,
    maxQuantity: item.maxQuantity,
    reorderPoint: item.reorderPoint,
    reorderQuantity: item.reorderQuantity,
    unitPrice: item.unitPrice,
    // Cost prices - required by backend, pass through existing values
    lastCostPrice: item.lastCostPrice || 0,
    averageCost: item.averageCost || 0,
    totalValue: item.value || 0, // Frontend uses 'value', backend uses 'totalValue'
    weight: item.weight || null,
    weightUnit: item.weightUnit || null,
    binNumber: item.binNumber || null,
    // Unit of Measure and Packaging
    unitOfMeasure: item.unitOfMeasure || "Each",
    packSize: item.packSize || null,
    cartonQuantity: item.cartonQuantity || null,
    weightPerUnit: item.weightPerUnit || null,
    weightPerUnitUnit: item.weightPerUnitUnit || null,
    dimensionLength: item.dimensionLength || null,
    dimensionWidth: item.dimensionWidth || null,
    dimensionHeight: item.dimensionHeight || null,
    dimensionUnit: item.dimensionUnit || null,
    cubicVolume: item.cubicVolume || null,
    purchaseAnalysisCode: item.purchaseAnalysisCode || null,
    salesAnalysisCode: item.salesAnalysisCode || null,
    movementInAnalysis: item.movementInAnalysis || null,
    movementOutAnalysis: item.movementOutAnalysis || null,
    aBCClassification: item.abcClassification || null,
    supplier: item.supplier || null,
    supplierContact: item.supplierContact || null,
    leadTimeDays: item.leadTimeDays || null,
    inactive: item.inactive,
  };
};

// Map frontend StockItem to backend input format for create (no stockItemID)
const mapFrontendToBackendCreateInput = (item: StockItem): StockItemCreateInput => {
  return {
    stockCode: item.code,
    description: item.description,
    category: item.category || null,
    location: item.locationCode || null,
    warehouseCode: item.warehouseCode || null,
    locationCode: item.locationCode || null,
    barcode: item.barcode || null,
    imageURL: item.image || null,
    physicalQuantity: item.physicalQuantity,
    allocatedQuantity: item.allocatedQuantity,
    quantityDueIn: item.quantityDueIn,
    minQuantity: item.minQuantity,
    maxQuantity: item.maxQuantity,
    reorderPoint: item.reorderPoint,
    reorderQuantity: item.reorderQuantity,
    unitPrice: item.unitPrice,
    // Cost prices - copy from original item during duplication
    lastCostPrice: item.lastCostPrice || 0,
    averageCost: item.averageCost || 0,
    totalValue: item.value || 0, // Frontend uses 'value', backend uses 'totalValue'
    weight: item.weight || null,
    weightUnit: item.weightUnit || null,
    binNumber: item.binNumber || null,
    // Unit of Measure and Packaging
    unitOfMeasure: item.unitOfMeasure || "Each",
    packSize: item.packSize || null,
    cartonQuantity: item.cartonQuantity || null,
    weightPerUnit: item.weightPerUnit || null,
    weightPerUnitUnit: item.weightPerUnitUnit || null,
    dimensionLength: item.dimensionLength || null,
    dimensionWidth: item.dimensionWidth || null,
    dimensionHeight: item.dimensionHeight || null,
    dimensionUnit: item.dimensionUnit || null,
    cubicVolume: item.cubicVolume || null,
    purchaseAnalysisCode: item.purchaseAnalysisCode || null,
    salesAnalysisCode: item.salesAnalysisCode || null,
    movementInAnalysis: item.movementInAnalysis || null,
    movementOutAnalysis: item.movementOutAnalysis || null,
    aBCClassification: item.abcClassification || null,
    supplier: item.supplier || null,
    supplierContact: item.supplierContact || null,
    leadTimeDays: item.leadTimeDays || null,
    inactive: item.inactive,
  };
};

// Map frontend StockItem to backend settings input format
const mapFrontendToSettingsInput = (item: StockItem): StockItemSettingsInput => {
  return {
    stockItemSettingID: 0, // Will be looked up by stockCode in backend
    stockItemID: item.stockItemID || 0,
    stockCode: item.code,
    isSellable: item.isSellable ?? true,
    isPurchasable: item.isPurchasable ?? true,
    isAssembly: item.isAssembly ?? false,
    isComponent: item.isComponent ?? false,
    allowDisassembly: item.allowDisassembly ?? false,
    trackingType: item.trackingType ?? "None",
    autoGenerateSerial: item.autoGenerateSerial ?? false,
    serialPrefix: item.serialPrefix ?? null,
    serialNextNumber: null,
    requireExpiryDate: item.requireExpiryDate ?? false,
    requireManufacturingDate: item.requireManufacturingDate ?? false,
    shelfLifeDays: item.shelfLifeDays ?? null,
    expiryWarningDays: null,
    allowNegativeStock: item.allowNegativeStock ?? false,
    minimumOrderQuantity: null,
    maximumOrderQuantity: null,
    orderMultiple: null,
    requireQualityCheck: false,
    quarantineDays: null,
    allowPriceOverride: true,
    minimumMarginPercent: null,
    maximumDiscountPercent: null,
  };
};

export const useStockItems = () => {
  const { fetchGraphQL } = useGraphQLFetch();
  const [stockItems, setStockItems] = useState<StockItem[]>([]);
  const [pageInfo, setPageInfo] = useState<PageInfo | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isUpdating, setIsUpdating] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchStockItems = useCallback(
    async (
      options: {
        first?: number;
        after?: string;
        filter?: StockItemFilterInput;
        sort?: StockItemSortInput;
      } = {},
    ): Promise<{ stockItems: StockItem[]; pageInfo: PageInfo | null }> => {
      setIsLoading(true);
      setError(null);

      const { first = 25, after, filter, sort } = options;

      const sortArray = sort ? [sort] : undefined;

      try {
        const result = await fetchGraphQL<GraphQLResponse["data"]>(STOCK_ITEMS_QUERY, {
          first,
          after,
          filter,
          sort: sortArray,
        });

        if (result.errors && result.errors.length > 0) {
          throw new Error(result.errors[0].message);
        }

        const connection = result.data?.stockItems?.stockItemList;
        const itemsList = connection?.items?.map((item) => mapBackendToFrontend(item)) || [];
        const pageInfoResult = connection?.pageInfo || null;

        setStockItems(itemsList);
        setPageInfo(pageInfoResult);

        return { stockItems: itemsList, pageInfo: pageInfoResult };
      } catch (err) {
        const message = err instanceof Error ? err.message : "Failed to fetch stock items";
        setError(message);
        console.error(err);
        toast.error(`Failed to load stock items from backend: ${message}`);
        return { stockItems: [], pageInfo: null };
      } finally {
        setIsLoading(false);
      }
    },
    [fetchGraphQL],
  );

  const fetchStockItemByCode = useCallback(
    async (stockCode: string): Promise<StockItem | null> => {
      setIsLoading(true);
      setError(null);
      try {
        // Fetch stock item and settings in parallel
        const [itemResult, settingsResult] = await Promise.all([
          fetchGraphQL<GraphQLResponse["data"]>(STOCK_ITEM_BY_CODE_QUERY, { stockCode }),
          fetchGraphQL<StockItemSettingsQueryResponse>(STOCK_ITEM_SETTINGS_BY_CODE_QUERY, { stockCode }),
        ]);

        if (itemResult.errors && itemResult.errors.length > 0) {
          throw new Error(itemResult.errors[0].message);
        }

        const connection = itemResult.data?.stockItems?.stockItemList;
        const items = connection?.items || [];
        if (items.length === 0) {
          return null;
        }

        const stockItem = mapBackendToFrontend(items[0]);

        // Debug: Log settings query response
        console.log("[DEBUG] Settings query response:", settingsResult);
        console.log("[DEBUG] Settings errors:", settingsResult.errors);
        console.log("[DEBUG] Settings data:", settingsResult.data);

        // Merge settings data into the stock item if available
        const settings = settingsResult.data?.stockItemSettings?.stockItemSettingsByCode;
        console.log("[DEBUG] Extracted settings:", settings);
        console.log("[DEBUG] allowNegativeStock from settings:", settings?.allowNegativeStock);

        if (settings) {
          stockItem.isSellable = settings.isSellable;
          stockItem.isPurchasable = settings.isPurchasable;
          stockItem.isAssembly = settings.isAssembly;
          stockItem.isComponent = settings.isComponent;
          stockItem.allowDisassembly = settings.allowDisassembly;
          stockItem.trackingType = settings.trackingType as StockItem["trackingType"];
          stockItem.autoGenerateSerial = settings.autoGenerateSerial;
          stockItem.serialPrefix = settings.serialPrefix;
          stockItem.requireExpiryDate = settings.requireExpiryDate;
          stockItem.requireManufacturingDate = settings.requireManufacturingDate;
          stockItem.shelfLifeDays = settings.shelfLifeDays;
          stockItem.allowNegativeStock = settings.allowNegativeStock;
          console.log("[DEBUG] After merge - stockItem.allowNegativeStock:", stockItem.allowNegativeStock);
        } else {
          console.log("[DEBUG] No settings found for stock code:", stockCode);
        }

        console.log("[DEBUG] Final stockItem:", stockItem);
        return stockItem;
      } catch (err) {
        const message = err instanceof Error ? err.message : "Failed to fetch stock item";
        setError(message);
        console.error(err);
        toast.error(`Failed to load stock item from backend: ${message}`);
        return null;
      } finally {
        setIsLoading(false);
      }
    },
    [fetchGraphQL],
  );

  const updateStockItem = useCallback(
    async (item: StockItem): Promise<boolean> => {
      setIsUpdating(true);
      setError(null);
      try {
        const input = mapFrontendToBackendInput(item);

        const result = await fetchGraphQL<UpdateMutationResponse["data"]>(UPDATE_STOCK_ITEM_MUTATION, {
          stockItem: input,
        });

        if (result.errors && result.errors.length > 0) {
          throw new Error(result.errors[0].message);
        }

        const updateResult = result.data?.stockItems?.stockItemsRecords?.update;
        const typename = updateResult?.__typename as string | undefined;

        // Check for validation failure
        if (typename === "FailedValidation") {
          const validationErrors = (updateResult as { errors?: Array<{ message: string }> })?.errors;
          const errorMessages = validationErrors?.map((e) => e.message).join("; ") || "Validation failed";
          throw new Error(errorMessages);
        }

        if (typename === "IntEntityId" && updateResult?.id) {
          // Also update stock item settings (for allowNegativeStock and other settings fields)
          const settingsInput = mapFrontendToSettingsInput(item);
          const settingsResult = await fetchGraphQL<UpdateSettingsMutationResponse["data"]>(
            UPDATE_STOCK_ITEM_SETTINGS_MUTATION,
            { settings: settingsInput },
          );

          if (settingsResult.errors && settingsResult.errors.length > 0) {
            console.warn("Failed to update stock item settings:", settingsResult.errors[0].message);
            // Don't fail the whole update if settings update fails
          }

          // Update successful - update local state
          setStockItems((prevItems) =>
            prevItems.map((existingItem) => (existingItem.stockItemID === item.stockItemID ? item : existingItem)),
          );
          return true;
        }

        // Handle other non-success types
        if (typename && typename !== "IntEntityId") {
          throw new Error(`Server error (${typename}) - please check the backend logs for details`);
        }

        throw new Error("Update failed: No response from server");
      } catch (err) {
        const message = err instanceof Error ? err.message : "Failed to update stock item";
        setError(message);
        console.error(err);
        toast.error(`Failed to update stock item: ${message}`);
        return false;
      } finally {
        setIsUpdating(false);
      }
    },
    [fetchGraphQL],
  );

  const fetchNextStockItemsPage = useCallback(
    async (
      options: {
        first?: number;
        filter?: StockItemFilterInput;
        sort?: StockItemSortInput;
      } = {},
    ): Promise<{ stockItems: StockItem[]; pageInfo: PageInfo | null }> => {
      if (!pageInfo?.endCursor) {
        return { stockItems: [], pageInfo: null };
      }
      return fetchStockItems({
        ...options,
        after: pageInfo.endCursor,
      });
    },
    [fetchStockItems, pageInfo],
  );

  const createStockItem = useCallback(
    async (item: StockItem): Promise<{ success: boolean; id?: number; error?: string }> => {
      setIsUpdating(true);
      setError(null);
      try {
        const input = mapFrontendToBackendCreateInput(item);

        const result = await fetchGraphQL<CreateMutationResponse["data"]>(CREATE_STOCK_ITEM_MUTATION, {
          stockItem: input,
        });

        if (result.errors && result.errors.length > 0) {
          throw new Error(result.errors[0].message);
        }

        const createResult = result.data?.stockItems?.stockItemsRecords?.create;
        const typename = createResult?.__typename as string | undefined;

        // Check if we got a successful IntEntityId result
        if (typename === "IntEntityId" && createResult?.id) {
          // Create successful - add to local state with the new ID
          const newItem = { ...item, stockItemID: createResult.id };
          setStockItems((prevItems) => [...prevItems, newItem]);
          return { success: true, id: createResult.id };
        }

        // Check for validation failure
        if (typename === "FailedValidation") {
          const validationErrors = (createResult as { errors?: Array<{ message: string }> })?.errors;
          const errorMessages = validationErrors?.map((e) => e.message).join("; ") || "Validation failed";
          throw new Error(errorMessages);
        }

        // We got a non-success union member (other error types)
        if (typename && typename !== "IntEntityId") {
          throw new Error(`Server error (${typename}) - please check the backend logs for details`);
        }

        // No result at all
        throw new Error("Create failed: No response from server");
      } catch (err) {
        const message = err instanceof Error ? err.message : "Failed to create stock item";
        setError(message);
        console.error(err);
        return { success: false, error: message };
      } finally {
        setIsUpdating(false);
      }
    },
    [fetchGraphQL],
  );

  const deleteStockItems = useCallback(
    async (stockItemIds: number[]): Promise<{ success: boolean; error?: string }> => {
      setIsUpdating(true);
      setError(null);
      try {
        const result = await fetchGraphQL<DeleteMutationResponse["data"]>(DELETE_STOCK_ITEMS_MUTATION, {
          ids: stockItemIds,
        });

        if (result.errors && result.errors.length > 0) {
          throw new Error(result.errors[0].message);
        }

        const deleteResult = result.data?.stockItems?.stockItemsRecords?.delete;

        // Check if deletion was successful
        if (deleteResult?.succeeded === true) {
          // Delete successful - remove from local state
          setStockItems((prevItems) =>
            prevItems.filter((item) => !stockItemIds.includes(item.stockItemID || 0)),
          );
          return { success: true };
        }

        // If we got a response but succeeded is false or missing, it's a failure
        if (deleteResult) {
          throw new Error("Delete operation was not successful");
        }

        throw new Error("Delete failed: No response from server");
      } catch (err) {
        const message = err instanceof Error ? err.message : "Failed to delete stock items";
        setError(message);
        console.error(err);
        return { success: false, error: message };
      } finally {
        setIsUpdating(false);
      }
    },
    [fetchGraphQL],
  );

  return {
    stockItems,
    pageInfo,
    isLoading,
    isUpdating,
    error,
    fetchStockItems,
    fetchNextStockItemsPage,
    fetchStockItemByCode,
    updateStockItem,
    createStockItem,
    deleteStockItems,
  };
};
